import * as React from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { CgClose } from 'react-icons/cg';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from '../../../redux/store';
import { OscillometryMeasurement } from '../../../Types/Common/src/measurement';

import './OscillometryOverreadModal.scss';

const { useState, useEffect } = React;

export type OverreadAcceptability = 'Accept' | 'Borderline Accept' | 'Reject';

export type BetweenManeuverStatement =
  | 'Poor resistance repeatability'
  | 'Only one or two manoeuvres'
  | 'No acceptable manoeuvres'
  | 'No comment'
  | 'System Setup/Practice'
  | 'Acceptable and repeatable data';

const ACCEPTABILITY_OPTIONS: { value: OverreadAcceptability; description: string }[] = [
  {
    value: 'Accept',
    description: 'Passed QA. At least 3 acceptable manoeuvres, meets repeatability criteria, no errors.',
  },
  {
    value: 'Borderline Accept',
    description: 'Grey zone. R5 reproducibility between 10%–15%, at least 3 acceptable manoeuvres, no errors.',
  },
  {
    value: 'Reject',
    description: 'Failed QA. Less than 3 acceptable manoeuvres, or errors/repeatability criteria not met (R5 CV >15%).',
  },
];

const BETWEEN_MANEUVER_OPTIONS: { value: BetweenManeuverStatement; description: string }[] = [
  {
    value: 'Poor resistance repeatability',
    description: 'Coefficient of variance (CV) of resistance at 5 Hz (R5) is more than 10%.',
  },
  {
    value: 'Only one or two manoeuvres',
    description: 'Session rejected as there are less than 3 acceptable manoeuvres.',
  },
  {
    value: 'No acceptable manoeuvres',
    description: 'Session failed QA as there are 0 acceptable manoeuvres.',
  },
  {
    value: 'No comment',
    description: 'No comment necessary.',
  },
  {
    value: 'System Setup/Practice',
    description: 'The session is a system setup or practice session using a dummy subject number.',
  },
  {
    value: 'Acceptable and repeatable data',
    description: 'Both resistance and reactance data are acceptable and repeatable.',
  },
];

export interface OverreadSubmission {
  acceptability: OverreadAcceptability;
  betweenManeuverStatement?: BetweenManeuverStatement;
  notes?: string;
}

interface OscillometryOverreadModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  bestMeasurements: OscillometryMeasurement | undefined;
  isReadOnly: boolean | undefined;
  onSave: (overread: OverreadSubmission) => Promise<void>;
}

export const OscillometryOverreadModal: React.FC<OscillometryOverreadModalProps> = ({
  show,
  setShow,
  bestMeasurements,
  isReadOnly,
  onSave,
}) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [acceptability, setAcceptability] = useState<OverreadAcceptability | ''>('');
  const [betweenManeuverStatement, setBetweenManeuverStatement] = useState<BetweenManeuverStatement | ''>('');
  const [notes, setNotes] = useState('');

  // Initialize from existing overread when modal opens
  useEffect(() => {
    if (show) {
      const existing = bestMeasurements?.overread;
      if (existing) {
        setAcceptability(existing.acceptability || '');
        setBetweenManeuverStatement(existing.betweenManeuverStatement || '');
        setNotes(existing.notes || '');
      } else {
        setAcceptability('');
        setBetweenManeuverStatement('');
        setNotes('');
      }
    }
  }, [show, bestMeasurements]);

  const handleClose = () => {
    if (isSaving) return;
    setShow(false);
  };

  const handleSave = async () => {
    if (isReadOnly || isSaving || !acceptability) return;
    setIsSaving(true);
    try {
      const submission: OverreadSubmission = {
        acceptability,
        ...(betweenManeuverStatement ? { betweenManeuverStatement } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
      await onSave(submission);
      setShow(false);
    } catch (e) {
      console.error('Error saving overread:', e);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = (): boolean => {
    if (!acceptability) return false;
    const existing = bestMeasurements?.overread;
    if (!existing) return true;
    if (existing.acceptability !== acceptability) return true;
    if ((existing.betweenManeuverStatement || '') !== betweenManeuverStatement) return true;
    if ((existing.notes || '') !== notes) return true;
    return false;
  };

  return (
    <Modal onHide={handleClose} show={show} size="lg" className="OscillometryOverreadModal">
      <Modal.Header className="d-flex flex-column pb-0">
        <Modal.Title>
          {translationText.overreadSession || 'Overread Session'}{' '}
          {isReadOnly && (translationText?.readOnly || '(Read-only)')}
        </Modal.Title>
        <p className="overread-modal-subtitle mb-1 mt-1">
          {translationText.overreadSessionDescription || 'Submit a quality assessment for this oscillometry session.'}
        </p>
        <CgClose size="30px" className="Close" onClick={handleClose} cursor="pointer" data-testid="OscOverreadModal-Close" />
      </Modal.Header>

      <Modal.Body>
        {/* Existing overread info */}
        {bestMeasurements?.overread && (
          <div className="existing-overread-banner mb-3 p-3">
            <strong>{translationText.previousOverread || 'Previous Overread'}:</strong>{' '}
            <span className={`overread-status overread-status--${bestMeasurements.overread.acceptability.replace(/\s+/g, '-').toLowerCase()}`}>
              {bestMeasurements.overread.acceptability}
            </span>
            {' '}&mdash; {translationText.by || 'by'} {bestMeasurements.overread.providerName}
            {bestMeasurements.overread.timestamp && (
              <span className="text-muted ml-2">
                ({new Date(bestMeasurements.overread.timestamp).toLocaleDateString()})
              </span>
            )}
          </div>
        )}

        {/* Acceptability Selection */}
        <Form.Group className="mb-4">
          <Form.Label className="font-weight-bold">
            {translationText.sessionAcceptability || 'Session Acceptability'} <span className="text-danger">*</span>
          </Form.Label>
          {ACCEPTABILITY_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={`acceptability-option p-3 mb-2 ${acceptability === option.value ? `selected selected--${option.value.replace(/\s+/g, '-').toLowerCase()}` : ''} ${isReadOnly ? 'disabled' : ''}`}
              onClick={() => !isReadOnly && !isSaving && setAcceptability(option.value)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !isReadOnly && !isSaving && setAcceptability(option.value); } }}
            >
              <Form.Check
                type="radio"
                name="acceptability"
                id={`acceptability-${option.value}`}
                label={<strong>{option.value}</strong>}
                checked={acceptability === option.value}
                onChange={() => setAcceptability(option.value)}
                disabled={!!isReadOnly || isSaving}
              />
              <small className="text-muted ml-4 d-block">{option.description}</small>
            </div>
          ))}
        </Form.Group>

        {/* Between-Manoeuvre Statement */}
        <Form.Group className="mb-4">
          <Form.Label className="font-weight-bold">
            {translationText.betweenManeuverStatement || 'Between-Manoeuvre Statement'}
          </Form.Label>
          <Form.Control
            as="select"
            value={betweenManeuverStatement}
            onChange={(e) => setBetweenManeuverStatement(e.target.value as BetweenManeuverStatement | '')}
            disabled={!!isReadOnly || isSaving}
          >
            <option value="">{translationText.selectStatement || '-- Select a statement (optional) --'}</option>
            {BETWEEN_MANEUVER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value}
              </option>
            ))}
          </Form.Control>
          {betweenManeuverStatement && (
            <small className="text-muted mt-1 d-block">
              {BETWEEN_MANEUVER_OPTIONS.find(o => o.value === betweenManeuverStatement)?.description}
            </small>
          )}
        </Form.Group>

        {/* Notes */}
        <Form.Group className="mb-3">
          <Form.Label className="font-weight-bold">
            {translationText.notes || 'Notes'}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={translationText.addOptionalNotes || 'Add optional notes...'}
            disabled={!!isReadOnly || isSaving}
            maxLength={500}
          />
          <small className="text-muted">{notes.length}/500</small>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          {translationText.cancel || 'Cancel'}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!!isReadOnly || isSaving || !acceptability || !hasChanges()}
        >
          {isSaving
            ? <><Spinner animation="border" size="sm" className="mr-2" />{translationText.saving || 'Saving...'}</>
            : translationText.submitOverread || 'Submit Overread'
          }
        </Button>
      </Modal.Footer>
    </Modal>
  );
};